import axios from 'axios';

export const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8080').replace(/\/$/, '');
const TOKEN_KEY = 'chron-token';
const LOGIN_STATE_KEY = 'chron-login-state';

export function normalizeReturnTo(value, origin = window.location.origin) {
    if (typeof value !== 'string' || value.length > 2048 || !value.startsWith('/') || value.startsWith('//')) return '/';
    if (Array.from(value).some(char => char === '\\' || char.charCodeAt(0) <= 32 || char.charCodeAt(0) === 127)) return '/';
    try {
        const url = new URL(value, origin);
        if (url.origin !== origin || url.pathname === '/login' || /%(?:2f|5c|0[0-9a-f]|1[0-9a-f]|7f)/i.test(url.pathname)) return '/';
        return `${url.pathname}${url.search}${url.hash}`;
    } catch { return '/'; }
}

export function createIdentity({ browser = window, transport = config => axios.request(config), apiBase = API_BASE_URL, now = Date.now } = {}) {
    let credential = null;
    let generation = 0;
    let started = false;
    let pending = null;
    let expiryTimer;
    let refreshTimer;
    let callbackTarget = null;
    const listeners = new Set();
    const requests = new Set();
    let state = Object.freeze({ status: 'initializing', user: null, expiresAt: null, sessionVersion: 0 });
    const readToken = () => { try { return browser.localStorage.getItem(TOKEN_KEY) || null; } catch { return null; } };
    const publish = (status, user = null, expiresAt = null) => {
        state = Object.freeze({ status, user, expiresAt, sessionVersion: generation });
        listeners.forEach(listener => listener());
    };
    function replaceCredential(value, persist = true) {
        generation += 1;
        credential = typeof value === 'string' && value ? value : null;
        pending = null;
        clearTimeout(expiryTimer);
        requests.forEach(controller => controller.abort());
        requests.clear();
        if (persist) {
            try {
                if (credential) browser.localStorage.setItem(TOKEN_KEY, credential);
                else browser.localStorage.removeItem(TOKEN_KEY);
            } catch { /* Current-page login still works when storage is unavailable. */ }
        }
        publish(credential ? 'initializing' : 'anonymous');
    }
    function logout() { replaceCredential(null); }
    function staleError() { const error = new Error('The signed-in account changed.'); error.name = 'IdentityChangedError'; return error; }
    function refresh() {
        if (!credential) { publish('anonymous'); return Promise.resolve(state); }
        if (pending) return pending;
        const version = generation;
        const controller = new AbortController();
        requests.add(controller);
        const task = (async () => {
            try {
                const response = await transport({ method: 'get', url: `${apiBase}/auth/me`, headers: { Authorization: `Bearer ${credential}` }, signal: controller.signal });
                if (version !== generation) return state;
                const profile = response.data;
                if (!profile || !Number.isInteger(profile.playerId) || typeof profile.staffPermissions !== 'boolean') throw new Error('Invalid identity response');
                const expiresAt = Number.isFinite(profile.expiresAt) ? profile.expiresAt : null;
                if (expiresAt !== null && expiresAt <= now()) { logout(); return state; }
                const user = Object.freeze({
                    playerId: profile.playerId, displayName: profile.displayName || null,
                    staffPermissions: profile.staffPermissions, authProvider: profile.authProvider || null,
                    primaryCharacter: profile.primaryCharacter ? Object.freeze({ characterId: profile.primaryCharacter.characterId, name: profile.primaryCharacter.name }) : null
                });
                if (state.user && (state.user.playerId !== user.playerId || state.user.staffPermissions !== user.staffPermissions)) {
                    generation += 1;
                    requests.forEach(request => { if (request !== controller) request.abort(); });
                }
                const verifiedVersion = generation;
                publish('authenticated', user, expiresAt);
                clearTimeout(expiryTimer);
                if (expiresAt !== null) expiryTimer = setTimeout(() => {
                    if (verifiedVersion !== generation) return;
                    if (expiresAt <= now()) logout(); else refresh();
                }, Math.min(expiresAt - now(), 2147483647));
            } catch (error) {
                if (version !== generation) return state;
                if ([401, 403].includes(error.response?.status)) logout();
                else publish('unavailable'); // Retain credentials for retry, never stale identity/permissions.
            } finally { requests.delete(controller); }
            return state;
        })();
        pending = task;
        task.finally(() => { if (pending === task) pending = null; });
        return task;
    }
    function synchronize() {
        const value = readToken(); // Read current storage, not a potentially stale event payload.
        if (value !== credential) { replaceCredential(value, false); return refresh(); }
        return refresh();
    }
    function onStorage(event) { if (event.key === TOKEN_KEY || event.key === null) synchronize(); }
    function onVisible() { if (browser.document.visibilityState === 'visible') synchronize(); }
    function initialize() {
        if (!started) {
            started = true;
            replaceCredential(readToken(), false);
            browser.addEventListener('storage', onStorage);
            browser.addEventListener('focus', synchronize);
            browser.document.addEventListener('visibilitychange', onVisible);
            refreshTimer = setInterval(() => { if (browser.document.visibilityState === 'visible') refresh(); }, 60000);
        }
        return state.status === 'initializing' ? refresh() : Promise.resolve(state);
    }
    async function setToken(value) { initialize(); replaceCredential(value); return refresh(); }
    function login(returnTo = `${browser.location.pathname}${browser.location.search}${browser.location.hash}`) {
        const target = normalizeReturnTo(returnTo, browser.location.origin);
        browser.location.assign(`/login?${new URLSearchParams({ returnTo: target })}`);
    }
    function loginWithDiscord(returnTo) {
        const url = new URL('/login', browser.location.origin);
        url.searchParams.set('returnTo', normalizeReturnTo(returnTo, browser.location.origin));
        const nonce = Array.from(browser.crypto.getRandomValues(new Uint8Array(32)), byte => byte.toString(16).padStart(2, '0')).join('');
        browser.sessionStorage.setItem(LOGIN_STATE_KEY, nonce);
        url.searchParams.set('loginState', nonce);
        browser.location.assign(`${apiBase}/auth/discord?${new URLSearchParams({ redirect: url.toString() })}`);
    }
    // Called before React renders. New callbacks use fragments; legacy query
    // callbacks are still accepted and immediately removed from browser history.
    function consumeLoginCallback() {
        if (browser.location.pathname !== '/login') return;
        const url = new URL(browser.location.href);
        const fragment = new URLSearchParams(url.hash.slice(1));
        const token = fragment.get('access_token') || url.searchParams.get('token');
        if (!token) return;
        let expected;
        try { expected = browser.sessionStorage.getItem(LOGIN_STATE_KEY); } catch { /* Fail closed. */ }
        const validState = expected && url.searchParams.get('loginState') === expected;
        const target = normalizeReturnTo(url.searchParams.get('returnTo'), url.origin);
        url.searchParams.delete('loginState');
        fragment.delete('access_token');
        url.searchParams.delete('token');
        url.hash = fragment.toString();
        if (!validState) url.searchParams.set('error', 'invalid_login');
        browser.history.replaceState(browser.history.state, '', `${url.pathname}${url.search}${url.hash}`);
        if (!validState) return;
        browser.sessionStorage.removeItem(LOGIN_STATE_KEY);
        callbackTarget = target;
        setToken(token);
    }
    function takeCallbackTarget() { const target = callbackTarget; callbackTarget = null; return target; }
    async function request(route, options = {}) {
        if (typeof route !== 'string' || !route.startsWith('/') || route.startsWith('//') || route.includes('\\')) throw new Error('API requests require a relative path');
        await initialize();
        const { authenticated = true, ...config } = options;
        if (authenticated && state.status !== 'authenticated') throw new Error('Sign-in could not be verified.');
        const version = generation;
        const controller = new AbortController();
        requests.add(controller);
        const headers = { ...config.headers };
        // The identity module alone controls credentials, including on public requests.
        for (const key of Object.keys(headers)) if (key.toLowerCase() === 'authorization') delete headers[key];
        if (authenticated) headers.Authorization = `Bearer ${credential}`;
        try {
            const response = await transport({ ...config, url: `${apiBase}${route}`, headers, signal: controller.signal });
            if (version !== generation) throw staleError();
            return response;
        } catch (error) {
            if (version !== generation) throw staleError();
            if (authenticated && error.response?.status === 401) logout();
            else if (authenticated && error.response?.status === 403) await refresh();
            // Do not propagate Axios config/request objects containing bearer headers.
            const safe = new Error(error.response?.data?.error || 'API request failed');
            if (error.response) safe.response = { status: error.response.status, data: error.response.data };
            throw safe;
        } finally { requests.delete(controller); }
    }
    function dispose() {
        clearTimeout(expiryTimer); clearInterval(refreshTimer);
        requests.forEach(controller => controller.abort());
        browser.removeEventListener('storage', onStorage);
        browser.removeEventListener('focus', synchronize);
        browser.document.removeEventListener('visibilitychange', onVisible);
        listeners.clear(); started = false;
    }
    return {
        initialize, refresh, login, loginWithDiscord, logout, setToken, request, consumeLoginCallback, takeCallbackTarget, dispose,
        getSnapshot: () => state,
        // Compatibility for existing React props only; never include tokens in public snapshots or DOM events.
        getToken: () => state.status === 'authenticated' ? credential : null,
        subscribe: listener => { listeners.add(listener); return () => listeners.delete(listener); }
    };
}

export const identity = createIdentity();
